import { parseISO } from "date-fns";
import { ArrowLeft, Copy, MapPin, Power, PowerOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Badge, Button, Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui";
import type {
  RelationChangeRequestRow,
  RelationDetailTab,
  RelationDetailUI,
  RelationEvidenceRow,
  RelationLineage,
} from "~/features/relations/types";
import { cn } from "~/lib/utils";
import { v2Route } from "~/v2/routes";
import { RelationChangeRequestsTab } from "./RelationChangeRequestsTab";
import { RelationDataTab } from "./RelationDataTab";
import { RelationEvidencesBox } from "./RelationEvidencesBox";
import { RelationLineageTab } from "./RelationLineageTab";

interface RelationDetailPresenterProps {
  detail: RelationDetailUI;
  onBack: () => void;
  onCopyId: (id: string) => void;
  onRequestToggleActivation: () => void;
  onSelectEntity: (id: string) => void;
  /** When set, the Area-Input button deep-links to this specific geo-item. */
  geoItemId?: string;
  activeTab: RelationDetailTab;
  onTabChange: (next: RelationDetailTab) => void;
  changeRequests: RelationChangeRequestRow[];
  evidences: RelationEvidenceRow[];
  lineage: RelationLineage;
  /** "page" = full-page route (own width + back button); "peek" = inside the Sheet. */
  variant?: "page" | "peek";
}

export function RelationDetailPresenter({
  detail,
  onBack,
  onCopyId,
  onRequestToggleActivation,
  onSelectEntity,
  geoItemId,
  activeTab,
  onTabChange,
  changeRequests,
  evidences,
  lineage,
  variant = "page",
}: RelationDetailPresenterProps) {
  const { t } = useTranslation();
  const isPeek = variant === "peek";
  const areaInputPath = geoItemId
    ? v2Route.areaInput.record.detail.$buildPath({ params: { recordId: detail.id, geoItemId } })
    : v2Route.areaInput.record.$buildPath({ params: { recordId: detail.id } });

  return (
    <div className={cn("space-y-5", isPeek ? "p-1" : "mx-auto max-w-[var(--content-max)] p-8")}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          {!isPeek && (
            <Button type="button" variant="ghost" size="sm" className="px-0 text-fg-2" onClick={onBack}>
              <ArrowLeft />
              <span>{t("ui.back")}</span>
            </Button>
          )}
          <div className="flex items-center gap-2">
            <h1 className={cn("text-fg-1", isPeek ? "text-16-bold" : "text-dewey-title")}>
              {detail.displayName || detail.id}
            </h1>
            {detail.isDeactivated && <Badge tone="default">{t("ui.deactivated")}</Badge>}
          </div>
          <div className="flex items-center gap-2 text-12-regular text-fg-3">
            <span className="tabular-nums">{detail.id}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5"
              aria-label={t("ui.copyId")}
              onClick={() => onCopyId(detail.id)}
            >
              <Copy className="h-3 w-3" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-x-4 text-12-regular text-fg-3">
            {detail.createdAt && (
              <span>
                {t("model.common.createdAt")}: {t("date.long", { date: parseISO(detail.createdAt) })}
              </span>
            )}
            {detail.updatedAt && (
              <span>
                {t("model.common.updatedAt")}: {t("date.long", { date: parseISO(detail.updatedAt) })}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Button type="button" variant="outline" size="sm" asChild>
            <Link to={areaInputPath} target="_blank">
              <MapPin />
              <span>{t("feature.entities.detail.areaInputPage")}</span>
            </Link>
          </Button>
          {detail.isDeactivated ? (
            <Button type="button" variant="ghost" size="sm" onClick={onRequestToggleActivation}>
              <Power />
              <span>{t("ui.activate")}</span>
            </Button>
          ) : (
            <Button type="button" variant="ghost" size="sm" onClick={onRequestToggleActivation}>
              <PowerOff />
              <span>{t("ui.deactivate")}</span>
            </Button>
          )}
        </div>
      </div>

      {evidences.length > 0 && <RelationEvidencesBox rows={evidences} />}

      <Tabs value={activeTab} onValueChange={(value) => onTabChange(value as RelationDetailTab)}>
        <TabsList>
          <TabsTrigger value="data">{t("ui.data")}</TabsTrigger>
          <TabsTrigger value="changeRequests">{t("model.changeRequest.modelNames")}</TabsTrigger>
          <TabsTrigger value="lineage">{t("ui.lineage")}</TabsTrigger>
        </TabsList>
        <TabsContent value="data" className="pt-4">
          <RelationDataTab detail={detail} onSelectEntity={onSelectEntity} />
        </TabsContent>
        <TabsContent value="changeRequests" className="pt-4">
          <RelationChangeRequestsTab rows={changeRequests} />
        </TabsContent>
        <TabsContent value="lineage" className="pt-4">
          <RelationLineageTab lineage={lineage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
